import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Stethoscope, FlaskConical, Pencil, ExternalLink, Trash2, RotateCcw, Save, X } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { Toggle } from '@/components/ui/Toggle'
import { Modal } from '@/components/ui/Modal'
import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { type PageSize } from '@/hooks/useCrudList'
import type { PaginationMeta } from '@/types/api'
import { normalizeForMatch } from '@/lib/validators'
import { toast } from '@/components/ui/Toast'
import { CredentialingProvidersService, type SpecialtySearchRow } from '@/services/modules/CredentialingService'
import { SpecialtiesService, type Specialty } from '@/services/modules/SpecialtiesService'
import { TestsService, type Test } from '@/services/modules/TestsService'
import { LookupService, type State, type City } from '@/services/modules/LookupService'

type Kind = 'specialty' | 'test'

export function CredentialingSpecialtyFilterPage() {
  const nav = useNavigate()
  const [kind, setKind] = useState<Kind | null>(null)

  // catálogos
  const [specialties, setSpecialties] = useState<Specialty[]>([])
  const [tests, setTests] = useState<Test[]>([])
  const [states, setStates] = useState<State[]>([])
  const [cities, setCities] = useState<City[]>([])

  // filtros
  const [itemId, setItemId] = useState('')
  const [stateId, setStateId] = useState('')
  // Várias cidades: o select funciona como "+ adicionar cidade".
  const [cityIds, setCityIds] = useState<string[]>([])

  // resultados
  const [rows, setRows] = useState<SpecialtySearchRow[]>([])
  const [searched, setSearched] = useState(false)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<PageSize>(25)

  const [editing, setEditing] = useState<SpecialtySearchRow | null>(null)
  const [deleting, setDeleting] = useState<SpecialtySearchRow | null>(null)

  useEffect(() => {
    LookupService.states().then(setStates).catch(() => setStates([]))
  }, [])
  useEffect(() => {
    if (kind === 'specialty' && specialties.length === 0) SpecialtiesService.list({ size: 0, status: 'ATIVO' }).then(r => setSpecialties(r.data)).catch(() => {})
    if (kind === 'test' && tests.length === 0) TestsService.list({ size: 0, status: 'ATIVO' }).then(r => setTests(r.data)).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind])
  useEffect(() => {
    if (!stateId) { setCities([]); return }
    LookupService.cities(stateId).then(setCities).catch(() => setCities([]))
  }, [stateId])

  const itemOptions = kind === 'specialty'
    ? specialties.map(s => ({ value: s.id, label: s.specialty }))
    : tests.map(t => ({ value: t.id, label: t.test }))

  async function runSearch() {
    if (!kind) return
    setLoading(true)
    try {
      const data = await CredentialingProvidersService.specialtyTestSearch({
        kind, item_id: itemId || undefined, state_id: stateId || undefined, city_ids: cityIds,
      })
      setRows(data.rows)
      setSearched(true)
      if (data.truncated) {
        toast.warning({ title: `Mostrando os primeiros ${data.limit.toLocaleString('pt-BR')} resultados`, description: 'Escolha uma especialidade ou menos cidades para ver tudo.' })
      }
      setPage(1)
    } catch {
      toast.error('Erro ao buscar.')
    } finally { setLoading(false) }
  }

  function resetKind() {
    setKind(null); setItemId(''); setStateId(''); setCityIds([])
    setRows([]); setSearched(false); setSearch('')
  }

  // filtro de texto + paginação local
  const filtered = useMemo(() => {
    const q = normalizeForMatch(search)
    if (!q) return rows
    return rows.filter(r =>
      normalizeForMatch(r.providerName).includes(q) ||
      normalizeForMatch(r.itemName).includes(q) ||
      normalizeForMatch(r.cityName ?? '').includes(q),
    )
  }, [rows, search])
  const lastPage = Math.max(1, Math.ceil(filtered.length / pageSize))
  const safePage = Math.min(page, lastPage)
  const paged = filtered.slice((safePage - 1) * pageSize, safePage * pageSize)
  const meta: PaginationMeta = {
    current_page: safePage, per_page: pageSize, last_page: lastPage, total: filtered.length,
    from: filtered.length ? (safePage - 1) * pageSize + 1 : null,
    to: Math.min(safePage * pageSize, filtered.length) || null,
  }

  const isSpec = kind === 'specialty'
  const itemHeader = isSpec ? 'Especialidade' : 'Exame'

  const columns: DataTableColumn<SpecialtySearchRow>[] = [
    { key: 'provider', header: 'Credenciado', alwaysVisible: true,
      render: r => <span className="font-medium text-gray-800">{r.providerName}</span> },
    { key: 'city', header: 'Cidade', render: r => <span className="text-gray-600">{r.cityName ?? '—'}{r.state ? ` / ${r.state}` : ''}</span> },
    { key: 'item', header: itemHeader, render: r => (
      <span className="text-gray-700">
        {r.itemName}
        {isSpec && r.telemedicine && <Badge variant="green" className="ml-2">Online</Badge>}
      </span>
    ) },
    { key: 'particular',     header: 'Particular',     align: 'center', className: 'w-28', render: r => <span className="text-gray-600">{r.formatted.particular}</span> },
    { key: 'amplaMais',      header: 'Ampla+',         align: 'center', className: 'w-28', render: r => <span className="text-gray-600">{r.formatted.amplaMais}</span> },
    { key: 'valorAssociado', header: 'Valor associado', align: 'center', className: 'w-32', render: r => <span className="text-gray-600">{r.formatted.valorAssociado}</span> },
    { key: 'valorAmplaMais', header: 'Valor Ampla+',   align: 'center', className: 'w-32', render: r => <span className="text-gray-600">{r.formatted.valorAmplaMais}</span> },
  ]

  const exp = useDataTableExport<SpecialtySearchRow>({
    filename: isSpec ? 'busca-especialidades' : 'busca-exames',
    service: { list: async () => ({ data: filtered }) },
    columns: [
      { header: 'Credenciado', accessor: r => r.providerName },
      { header: 'Cidade',      accessor: r => `${r.cityName ?? ''}${r.state ? ' / ' + r.state : ''}` },
      { header: itemHeader,    accessor: r => r.itemName },
      ...(isSpec ? [{ header: 'Telemedicina', accessor: (r: SpecialtySearchRow) => r.telemedicine ? 'Atende' : 'Não atende' }] : []),
      { header: 'Particular',      accessor: r => r.formatted.particular },
      { header: 'Ampla+',          accessor: r => r.formatted.amplaMais },
      { header: 'Valor associado', accessor: r => r.formatted.valorAssociado },
      { header: 'Valor Ampla+',    accessor: r => r.formatted.valorAmplaMais },
    ],
  })

  async function confirmDelete() {
    if (!deleting || !kind) return
    try {
      await CredentialingProvidersService.removeSpecialtyOrTest(deleting.providerId, deleting.pivotId, kind)
      toast.success({ title: 'Removido', description: `${deleting.itemName} removido de ${deleting.providerName}.` })
      setDeleting(null)
      await runSearch()
    } catch { toast.error('Erro ao remover.') }
  }

  return (
    <>
      <PageHeader
        title="Filtro de especialidades"
        subtitle="Pesquise valores de especialidades ou exames entre os credenciados"
        crumbs={[{ label: 'Credenciamento' }, { label: 'Filtro de especialidades' }]}
      />

      {/* Passo 1: escolher o que filtrar */}
      {!kind ? (
        <div className="bg-white rounded-md ring-1 ring-gray-200 p-6 sm:p-8">
          <h2 className="text-sm font-semibold text-gray-800 mb-1">O que deseja filtrar?</h2>
          <p className="text-xs text-gray-500 mb-5">Selecione abaixo para começar a pesquisa.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
            <button type="button" onClick={() => setKind('specialty')}
              className="cursor-pointer flex flex-col items-center gap-2 rounded-md ring-1 ring-gray-200 p-6 hover:ring-brand-400 hover:bg-brand-50/40 transition">
              <Stethoscope size={28} className="text-brand-500" />
              <span className="text-sm font-medium text-gray-800">Especialidade</span>
            </button>
            <button type="button" onClick={() => setKind('test')}
              className="cursor-pointer flex flex-col items-center gap-2 rounded-md ring-1 ring-gray-200 p-6 hover:ring-brand-400 hover:bg-brand-50/40 transition">
              <FlaskConical size={28} className="text-brand-500" />
              <span className="text-sm font-medium text-gray-800">Exame</span>
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Passo 2: filtros */}
          <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6 mb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {isSpec ? <Stethoscope size={16} className="text-brand-500" /> : <FlaskConical size={16} className="text-brand-500" />}
                <h2 className="text-sm font-semibold text-gray-800">Filtrar {isSpec ? 'especialidade' : 'exame'}</h2>
              </div>
              <Button size="xs" variant="ghost" iconLeft={<RotateCcw size={12} />} onClick={resetKind}>Trocar tipo</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <Select
                label={isSpec ? 'Especialidade' : 'Exame'}
                value={itemId} onChange={(e) => setItemId(e.target.value)}
                placeholder={isSpec ? 'Todas' : 'Todos'}
                options={itemOptions}
              />
              <Select
                label="Estado" value={stateId}
                onChange={(e) => { setStateId(e.target.value); setCityIds([]) }}
                placeholder="Todos" options={states.map(s => ({ value: s.id, label: s.abbreviation }))}
              />
              <Select
                label="Cidades" value=""
                onChange={(e) => { const v = e.target.value; if (v) setCityIds(ids => ids.includes(v) ? ids : [...ids, v]) }}
                placeholder={!stateId ? 'Selecione o estado' : cityIds.length ? '+ Adicionar outra cidade' : '+ Adicionar cidade (todas)'}
                disabled={!stateId} searchPlaceholder="Buscar cidade" clearable={false}
                options={cities.filter(c => !cityIds.includes(c.id)).map(c => ({ value: c.id, label: c.city }))}
              />
              <Button onClick={runSearch} loading={loading} iconLeft={<Search size={14} />} className="w-full">Pesquisar</Button>
            </div>
            {cityIds.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-gray-500">Cidades:</span>
                {cityIds.map(id => (
                  <span key={id} className="inline-flex items-center gap-1 rounded-full bg-brand-50 py-0.5 pl-2.5 pr-1 text-xs text-brand-700 ring-1 ring-brand-200">
                    {cities.find(c => c.id === id)?.city ?? 'Cidade'}
                    <button type="button" onClick={() => setCityIds(ids => ids.filter(x => x !== id))}
                      aria-label={`Remover ${cities.find(c => c.id === id)?.city ?? 'cidade'}`}
                      className="inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-full hover:bg-brand-100">
                      <X size={12} />
                    </button>
                  </span>
                ))}
                <button type="button" onClick={() => setCityIds([])} className="cursor-pointer text-xs font-medium text-gray-500 hover:text-gray-700 hover:underline">limpar</button>
              </div>
            )}
          </div>

          {/* Resultados */}
          {searched && (
            <>
              <div className="mb-3 max-w-xs">
                <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                  placeholder="Filtrar resultados" iconLeft={<Search size={14} />} clearable />
              </div>
              <DataTable
                tableId="credentialing.specialty-search"
                columns={columns}
                rows={paged}
                loading={loading}
                getRowKey={r => r.pivotId}
                emptyMessage="Nenhum resultado para os filtros selecionados."
                exportable
                onExportPage={() => exp.exportPage(paged)}
                onExportAll={exp.exportAll}
                rowActions={(r) => [
                  { key: 'edit', label: 'Ver e editar', icon: <Pencil size={14} />, onClick: () => setEditing(r) },
                  { key: 'provider', label: 'Ver credenciado', icon: <ExternalLink size={14} />, onClick: () => nav(`/credentialing/providers/${r.providerId}/editar`) },
                  { key: 'delete', label: 'Excluir', icon: <Trash2 size={14} />, danger: true, divider: true, onClick: () => setDeleting(r) },
                ]}
              />
              <Pagination meta={meta} size={pageSize} onChange={setPage} onSizeChange={setPageSize} />
            </>
          )}
        </>
      )}

      {editing && kind && (
        <EditValuesModal
          kind={kind}
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await runSearch() }}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title="Excluir?"
        description={`Remover "${deleting?.itemName}" de ${deleting?.providerName}?`}
        confirmLabel="Excluir"
        variant="danger"
      />
    </>
  )
}

// =============================================================================
// Modal de edição dos valores do pivot
// =============================================================================
function EditValuesModal({ kind, row, onClose, onSaved }: {
  kind: Kind
  row: SpecialtySearchRow
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const isSpec = kind === 'specialty'
  const [price, setPrice] = useState<number | null>(row.priceInCents / 100)
  const [discount, setDiscount] = useState<number | null>(row.discountValueInCents / 100)
  const [copay, setCopay] = useState<number | null>(row.copaymentInCents / 100)
  const [online, setOnline] = useState(!!row.telemedicine)
  const [saving, setSaving] = useState(false)

  async function save() {
    setSaving(true)
    try {
      await CredentialingProvidersService.updateSpecialtyOrTest(row.providerId, row.pivotId, {
        type: kind,
        price_in_cents: Math.round((price ?? 0) * 100),
        discount_value_in_cents: Math.round((discount ?? 0) * 100),
        copayment_in_cents: Math.round((copay ?? 0) * 100),
        ...(isSpec ? { telemedicine: online } : {}),
      })
      toast.success({ title: 'Valores atualizados', description: row.itemName })
      await onSaved()
    } catch { toast.error('Erro ao salvar.') }
    finally { setSaving(false) }
  }

  return (
    <Modal
      open onClose={onClose}
      title={`${row.itemName} — ${row.providerName}`}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button onClick={save} loading={saving} iconLeft={<Save size={14} />}>Salvar</Button>
      </>}
    >
      <div className="grid grid-cols-1 gap-4">
        <CurrencyInput label="Valor" value={price} onChange={setPrice} />
        <CurrencyInput label="Desconto Ampla+" value={discount} onChange={setDiscount} />
        <CurrencyInput label="Coparticipação coberta pela Ampla+" value={copay} onChange={setCopay} />
        {isSpec && (
          <div className="border-t border-gray-100 pt-4">
            <Toggle checked={online} onChange={setOnline} label="Faz atendimento online?" />
          </div>
        )}
      </div>
    </Modal>
  )
}
